// /admin/v2/clips — Video clips (BRIEFS C6.1, SPEC §7). A clip is a part of a YouTube
// video (start..end) placed under one or more notes units; Harsh sees it as a
// line at the start of the unit: "Concept video: <title> · N min" or "Question video: …"
// (C12.1, D-106 — the Kind choice below, default Concept). Add (paste any YouTube link,
// start/end as mm:ss, title, kind, units from a searchable picker), edit, switch off / on
// (clips are never deleted), and set the order of the clips inside a unit.
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  v2AdminChapters, v2Clips, v2ClipCreate, v2ClipUpdate, v2ClipUnits, v2ClipUnit, v2ClipOrder, errText,
} from '../../../api/v2admin';
import V2Tabs, { Msg, chapterLabel } from './V2Tabs';

const muted = { fontSize: 12, color: 'var(--text-muted)' };
const unitText = (u) => (u.number ? `${u.number} ${u.title}` : u.title || u.code);
export const KIND_TEXT = { concept: 'Concept video', question: 'Question video' };
const kindOf = (c) => (c && c.kind === 'question' ? 'question' : 'concept');

// Kind choice (C12.1): Concept video (default) / Question video.
export function KindChoice({ value, onChange, testid = 'v2-clip-kind', name }) {
  return (
    <div role="radiogroup" aria-label="Kind of video" data-testid={testid} style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', minHeight: 38 }}>
      {['concept', 'question'].map((k) => (
        <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14, cursor: 'pointer' }}>
          <input type="radio" name={name || testid} value={k} checked={value === k}
            data-testid={`${testid}-${k}`} onChange={() => onChange(k)} />
          {KIND_TEXT[k]}
        </label>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ unit picker

export function UnitPicker({ value, onChange }) {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const { data, isFetching } = useQuery({
    queryKey: ['v2-clip-units', term],
    queryFn: () => v2ClipUnits({ q: term }),
    enabled: term.length >= 2,
  });
  const chosen = new Set(value.map((u) => u.code));
  const results = (data ? data.units : []).filter((u) => !chosen.has(u.code));

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: value.length ? 8 : 0 }}>
        {value.map((u) => (
          <span key={u.code} data-testid={`v2-clip-chosen-${u.code}`} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 10px',
            borderRadius: 16, background: '#EEF2FF', border: '1px solid #C7D2FE', fontSize: 13,
          }}>
            <strong>{u.code}</strong> {u.title}
            <button type="button" className="btn btn-ghost btn-sm" aria-label={`Remove ${u.code}`}
              onClick={() => onChange(value.filter((x) => x.code !== u.code))}
              style={{ padding: '0 8px', minHeight: 26 }}>×</button>
          </span>
        ))}
      </div>
      <input className="form-input" data-testid="v2-clip-unit-search" value={q}
        onChange={(e) => setQ(e.target.value)} placeholder="Search a unit: type words of its title or its code (e.g. Replication, XII-05.4)"
        style={{ width: '100%' }} />
      {term.length >= 2 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 4, maxHeight: 240, overflowY: 'auto', background: 'var(--surface)' }}>
          {isFetching && !data && <div style={{ padding: 10, ...muted }}>Searching…</div>}
          {data && results.length === 0 && <div style={{ padding: 10, ...muted }}>No unit found.</div>}
          {results.map((u) => (
            <button key={u.code} type="button" data-testid={`v2-clip-unit-${u.code}`}
              onClick={() => { onChange([...value, u]); setQ(''); setTerm(''); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 0,
                borderBottom: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 14,
              }}>
              <strong>{u.code}</strong> · {unitText(u)}
              <div style={muted}>{u.chapter}{u.clips ? ` · ${u.clips} clip${u.clips > 1 ? 's' : ''} already` : ''}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ add / edit form

function ClipForm({ clip, onSaved, onCancel }) {
  const [url, setUrl] = useState(clip ? clip.url : '');
  const [start, setStart] = useState(clip ? clip.start_text : '0:00');
  const [end, setEnd] = useState(clip ? clip.end_text || '' : '');
  const [title, setTitle] = useState(clip ? clip.title : '');
  const [kind, setKind] = useState(kindOf(clip));
  const [units, setUnits] = useState(clip ? clip.units.filter((u) => !u.is_removed) : []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    const body = { url, start, end, title, kind, units: units.map((u) => u.code) };
    try {
      const out = clip ? await v2ClipUpdate(clip.id, body) : await v2ClipCreate(body);
      onSaved(out, !!clip);
      if (!clip) { setUrl(''); setStart('0:00'); setEnd(''); setTitle(''); setKind('concept'); setUnits([]); }
    } catch (x) {
      setErr(errText(x, 'Could not save the clip.'));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} data-testid={clip ? `v2-clip-edit-${clip.id}` : 'v2-clip-add'}
      style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
      <label style={{ gridColumn: '1 / -1' }}>
        <div style={muted}>YouTube link (any kind: watch, youtu.be, shorts, embed) or the video id</div>
        <input className="form-input" data-testid="v2-clip-url" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=…" style={{ width: '100%' }} required />
      </label>
      <label>
        <div style={muted}>Start (mm:ss)</div>
        <input className="form-input" data-testid="v2-clip-start" value={start} onChange={(e) => setStart(e.target.value)}
          placeholder="0:00" style={{ width: '100%' }} />
      </label>
      <label>
        <div style={muted}>End (mm:ss)</div>
        <input className="form-input" data-testid="v2-clip-end" value={end} onChange={(e) => setEnd(e.target.value)}
          placeholder="8:00" style={{ width: '100%' }} required />
      </label>
      <label style={{ gridColumn: 'span 2' }}>
        <div style={muted}>Title</div>
        <input className="form-input" data-testid="v2-clip-title" value={title} onChange={(e) => setTitle(e.target.value)}
          maxLength={200} style={{ width: '100%' }} required />
      </label>
      <div style={{ gridColumn: '1 / -1' }}>
        <div style={muted}>Kind — a Concept video teaches the topic; a Question video solves questions on it. Harsh sees concept videos first.</div>
        <KindChoice value={kind} onChange={setKind} name={clip ? `v2-clip-kind-${clip.id}` : 'v2-clip-kind-new'} />
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <div style={muted}>Units — the clip shows at the start of each unit chosen here</div>
        <UnitPicker value={units} onChange={setUnits} />
      </div>
      {err && <div role="alert" style={{ gridColumn: '1 / -1', color: '#B91C1C' }}>{err}</div>}
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary" data-testid="v2-clip-save" disabled={busy}>
          {busy ? 'Saving…' : (clip ? 'Save changes' : 'Add clip')}
        </button>
        {onCancel && <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}

// ------------------------------------------------------------------ order inside a unit

function UnitOrder({ code, onDone }) {
  const { data, refetch, isLoading } = useQuery({ queryKey: ['v2-clip-unit', code], queryFn: () => v2ClipUnit(code) });
  const [err, setErr] = useState('');
  const move = async (i, dir) => {
    const ids = data.clips.map((c) => c.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setErr('');
    try { await v2ClipOrder(code, ids); await refetch(); onDone(); } catch (x) { setErr(errText(x, 'Could not change the order.')); }
  };
  if (isLoading || !data) return <div style={muted}>Loading…</div>;
  return (
    <div data-testid={`v2-clip-order-${code}`} style={{ marginTop: 8 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{code} · {unitText(data.unit)} — order on the notes page</div>
      <ol style={{ margin: 0, paddingLeft: 22 }}>
        {data.clips.map((c, i) => (
          <li key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ flexGrow: 1 }}>{c.title} <span style={muted}>· {KIND_TEXT[kindOf(c)]}</span> {!c.is_active && <span className="badge badge-red">Off</span>}</span>
            <button type="button" className="btn btn-ghost btn-sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${c.title} up`}>Up</button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={i === data.clips.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${c.title} down`}>Down</button>
          </li>
        ))}
      </ol>
      {err && <div role="alert" style={{ color: '#B91C1C' }}>{err}</div>}
    </div>
  );
}

// ------------------------------------------------------------------ page

export default function AdminV2Clips() {
  const [chapterId, setChapterId] = useState('');
  const [q, setQ] = useState('');
  const [showOff, setShowOff] = useState(true);
  const [editing, setEditing] = useState(null);
  const [ordering, setOrdering] = useState(null);
  const [msg, setMsg] = useState(null);

  const chapters = useQuery({ queryKey: ['v2-admin-chapters'], queryFn: v2AdminChapters });
  const params = { ...(chapterId ? { chapter_id: chapterId } : {}), ...(q.trim() ? { q: q.trim() } : {}), ...(showOff ? {} : { active_only: 1 }) };
  const list = useQuery({ queryKey: ['v2-clips', params], queryFn: () => v2Clips(params) });
  const clips = list.data ? list.data.clips : [];

  // units with 2 or more clips (their order matters)
  const shared = useMemo(() => {
    const n = {};
    clips.forEach((c) => c.units.forEach((u) => { n[u.code] = (n[u.code] || 0) + 1; }));
    return Object.keys(n).filter((k) => n[k] > 1).sort();
  }, [clips]);

  const saved = (c, wasEdit) => {
    setEditing(null);
    setMsg({ kind: 'ok', text: wasEdit ? `Saved "${c.title}".` : `Added "${c.title}" (${c.minutes} min) under ${c.units.map((u) => u.code).join(', ')}.` });
    list.refetch();
  };
  const toggle = async (c) => {
    try {
      const out = await v2ClipUpdate(c.id, { is_active: !c.is_active });
      setMsg({ kind: 'ok', text: out.is_active ? `"${c.title}" is on again.` : `"${c.title}" is switched off (hidden from the notes).` });
      list.refetch();
    } catch (x) { setMsg({ kind: 'err', text: errText(x) }); }
  };

  const chapterList = chapters.data ? (chapters.data.chapters || chapters.data) : [];

  return (
    <div>
      <V2Tabs title="Video clips"
        intro={'Parts of YouTube videos shown at the start of a notes unit as one line each: "Concept video: <title> · N min" or "Question video: <title> · N min". Clips are never deleted: switch one off to hide it.'} />
      <Msg msg={msg} />

      <div className="card" style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, marginBottom: 12 }}>Add a clip</h2>
        <ClipForm onSaved={saved} />
      </div>

      <div className="card">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 18, flexGrow: 1 }}>
            Clips {list.data && <span style={muted}>({list.data.counts.active} on, {list.data.counts.total} in all)</span>}
          </h2>
          <select className="form-select" value={chapterId} onChange={(e) => setChapterId(e.target.value)} aria-label="Chapter">
            <option value="">All chapters</option>
            {Array.isArray(chapterList) && chapterList.map((c) => (
              <option key={c.chapter_id} value={c.chapter_id}>{chapterLabel(c)}</option>
            ))}
          </select>
          <input className="form-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title" aria-label="Search title" />
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
            <input type="checkbox" checked={showOff} onChange={(e) => setShowOff(e.target.checked)} /> Show switched-off clips
          </label>
        </div>
        {list.isLoading && <div className="spinner" />}
        {list.data && clips.length === 0 && <div style={muted}>No clips yet.</div>}
        {clips.length > 0 && (
          <table className="table" data-testid="v2-clip-table">
            <thead><tr><th>Title</th><th>Kind</th><th>Part</th><th>Units</th><th>Status</th><th /></tr></thead>
            <tbody>
              {clips.map((c) => (
                <React.Fragment key={c.id}>
                  <tr data-testid={`v2-clip-row-${c.id}`}>
                    <td style={{ verticalAlign: 'top' }}>
                      <strong>{c.title}</strong>
                      <div style={muted}><a href={c.url} target="_blank" rel="noreferrer">Open on YouTube</a> · {c.youtube_id}</div>
                    </td>
                    <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }} data-testid={`v2-clip-kind-cell-${c.id}`}>
                      <span className={`badge ${kindOf(c) === 'question' ? 'badge-yellow' : 'badge-blue'}`}>{KIND_TEXT[kindOf(c)]}</span>
                    </td>
                    <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      {c.start_text} – {c.end_text}<div style={muted}>{c.minutes} min</div>
                    </td>
                    <td style={{ verticalAlign: 'top' }}>
                      {c.units.map((u) => (
                        <div key={u.code} style={{ fontSize: 13 }}><strong>{u.code}</strong> {u.title}{u.is_removed ? ' (unit removed)' : ''}</div>
                      ))}
                    </td>
                    <td style={{ verticalAlign: 'top' }}>
                      <span className={`badge ${c.is_active ? 'badge-green' : 'badge-red'}`}>{c.is_active ? 'On' : 'Off'}</span>
                    </td>
                    <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(editing === c.id ? null : c.id)}>Edit</button>{' '}
                      <button type="button" className="btn btn-ghost btn-sm" data-testid={`v2-clip-toggle-${c.id}`} onClick={() => toggle(c)}>
                        {c.is_active ? 'Switch off' : 'Switch on'}
                      </button>
                    </td>
                  </tr>
                  {editing === c.id && (
                    <tr><td colSpan={6}><ClipForm clip={c} onSaved={saved} onCancel={() => setEditing(null)} /></td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {shared.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <h2 style={{ fontSize: 18, marginBottom: 6 }}>Order inside a unit</h2>
          <div style={{ ...muted, marginBottom: 10 }}>These units have more than one clip. On the notes page concept videos come first, then question videos — each in this order.</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {shared.map((code) => (
              <button key={code} type="button" className={`btn btn-sm ${ordering === code ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setOrdering(ordering === code ? null : code)}>{code}</button>
            ))}
          </div>
          {ordering && <UnitOrder code={ordering} onDone={() => list.refetch()} />}
        </div>
      )}
    </div>
  );
}
